import { Alert, Button, Card, Field, Input } from '@platform/ui';
import { useState, type FormEvent } from 'react';

import { useMessages } from '../i18n';
import { problemCode, problemText } from '../lib/problems';
import { useCompleteSecondFactor } from '../lib/session';

/**
 * The second step of a sign-in (docs/07 §4): the code an authenticator app
 * shows, or a recovery code, redeeming the token the password answered with.
 * Shared by signing in and by accepting an invitation as someone enrolled.
 *
 * Once the code is accepted the session exists and the panel's own gate takes
 * the person in. A sign-in that has expired, been spent or locked cannot be
 * answered with another code, so the form offers starting again instead.
 */
export function SecondFactorForm({ token, onStartOver }: { token: string; onStartOver: () => void }) {
    const t = useMessages();
    const s = t.auth.secondFactor;
    const complete = useCompleteSecondFactor();
    const [code, setCode] = useState('');

    async function onSubmit(event: FormEvent) {
        event.preventDefault();
        try {
            await complete.mutateAsync({ token, code: code.trim() });
        } catch {
            // Said above the field, from the mutation's own error state.
        }
    }

    const expired = problemCode(complete.error) === 'auth.second_factor_expired';

    return (
        <Card title={s.title} description={s.hint}>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                {complete.isError && <Alert tone="danger">{problemText(t, complete.error)}</Alert>}
                {expired ? (
                    <Button type="button" block onClick={onStartOver}>
                        {s.startOver}
                    </Button>
                ) : (
                    <>
                        <Field label={s.code} required>
                            {({ id, describedBy }) => (
                                <Input
                                    id={id}
                                    aria-describedby={describedBy}
                                    name="code"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    value={code}
                                    onChange={(event) => setCode(event.target.value)}
                                />
                            )}
                        </Field>
                        {/* Still busy once accepted: the page is leaving for the panel. */}
                        <Button type="submit" block loading={complete.isPending || complete.isSuccess} disabled={code.trim().length < 6}>
                            {s.submit}
                        </Button>
                    </>
                )}
            </form>
        </Card>
    );
}
